import {useCallback, useState, type ReactNode} from "react";
import {ListNotesModal} from "~/components/notes/listNotesModal";
import {ListNotesSidePanel} from "~/components/notes/listNotesSidePanel";
import type {ListNotesPanelContext, ListNotesPanelOpenContext} from "~/components/notes/listNotesPanelTypes";

export function useListNotesPanel() {
    const [notesPanelContext, setNotesPanelContext] = useState<ListNotesPanelContext | null>(null);

    /** Opens notes in the aside side panel (default for table, list, kanban, and other non-focus views). */
    const openNotesPanel = useCallback((context: ListNotesPanelOpenContext) => {
        setNotesPanelContext({
            ...context,
            presentation: "sidebar",
            showHistory: context.showHistory ?? true,
        });
    }, []);

    /** Opens notes in a centered modal — use only from Focus mode engagement actions. */
    const openNotesModal = useCallback((context: ListNotesPanelOpenContext) => {
        setNotesPanelContext({
            ...context,
            presentation: "modal",
            showHistory: context.showHistory ?? false,
        });
    }, []);

    const closeNotesPanel = useCallback(() => {
        setNotesPanelContext(null);
    }, []);

    const listNotesPanelChrome: ReactNode =
        notesPanelContext !== null ? (
            notesPanelContext.presentation === "modal" ? (
                <ListNotesModal
                    context={notesPanelContext}
                    onClose={closeNotesPanel}
                />
            ) : (
                <ListNotesSidePanel
                    context={notesPanelContext}
                    onClose={closeNotesPanel}
                />
            )
        ) : null;

    return {
        notesPanelContext,
        openNotesPanel,
        openNotesModal,
        closeNotesPanel,
        listNotesPanelChrome,
    };
}
